import { useState } from "react";
import { Box } from "@mui/material";
import { FieldValues } from "react-hook-form";
import { DateTime } from "luxon";
import { Form } from "react-admin";
import { CreateTheoryCourseForm } from "./CreateTheoryCourseForm";
import { getNumLessons, PlanTheoryCourseForm } from "./PlanTheoryCourseForm";
import { TheoryLecture, TheoryLectureCategory } from "../../model/TheoryLecture";
import { useCurrentDrivingSchool } from "../../hooks/useCurrentDrivingSchool";

export interface TheoryCourseLesson {
  id: string;
  date: DateTime;
  label: string;
  parentTableNode: HTMLTableElement | null;
  start: DateTime;
  instructorId: string;
  location: string;
  theoryLecture: TheoryLecture;
}

export const TheoryCourseWizard = (props: { goBack?: () => void; onSuccess: () => void }) => {
  const { goBack, onSuccess } = props;
  const [step, setStep] = useState<1 | 2>(1);
  const [lessons, setLessons] = useState<TheoryCourseLesson[]>([]);
  const drivingSchool = useCurrentDrivingSchool();
  const { branches, defaultBranch } = drivingSchool;
  const [error, setError] = useState<string | null>(null);

  const handleNextStep = (data: FieldValues) => {
    const { instructorId, location, start, content, breakDuration } = data as {
      start: DateTime;
      location: string;
      instructorId: string;
      content: Array<TheoryLectureCategory>;
      breakDuration: number;
    };
    const numLessons = getNumLessons(content);
    if (lessons.length !== numLessons) {
      setError(`Bitte wähle ${numLessons} Termine aus.`);
      return;
    }
    // lessons array is sorted by date
    const lessonsMap: Record<string, boolean> = {};
    let lessonIndexPerDay = 0;
    const mappedLessons = lessons.map((lesson, index) => {
      if (lessonsMap[lesson.date.toISODate()]) {
        lessonIndexPerDay++;
      } else {
        lessonIndexPerDay = 0;
        lessonsMap[lesson.date.toISODate()] = true;
      }
      const { date } = lesson;

      const combinedDateTime = DateTime.fromObject(
        {
          year: date.year,
          month: date.month,
          day: date.day,
          hour: start.hour,
          minute: start.minute,
          second: start.second,
          millisecond: start.millisecond,
        },
        { zone: start.zone },
      ).plus({ minutes: lessonIndexPerDay > 0 ? lessonIndexPerDay * (90 + breakDuration) : 0 });

      return {
        ...lesson,
        instructorId,
        location,
        start: combinedDateTime,
        theoryLecture: getTheoryLecture(index, content),
      };
    });
    setLessons(mappedLessons);
    setStep(2);
  };

  if (!drivingSchool) return null;

  return (
    <Box width="1100px">
      {step === 1 ? (
        <Form
          onSubmit={handleNextStep}
          defaultValues={() => ({
            content: ["Grundstoff", "Zusatzstoff_B"],
            instructorId: "",
            location: branches.length > 1 ? "" : defaultBranch.uid,
            start: DateTime.fromObject({ hour: 0, minute: 0 }),
            breakDuration: 0,
          })}
        >
          <PlanTheoryCourseForm
            goBack={goBack}
            lessons={lessons}
            setLessons={setLessons}
            drivingSchool={drivingSchool}
            error={error}
          />
        </Form>
      ) : (
        <CreateTheoryCourseForm
          goBack={() => setStep(1)}
          initialLessons={lessons}
          drivingSchool={drivingSchool}
          onSuccess={onSuccess}
        />
      )}
    </Box>
  );
};

function getTheoryLecture(index: number, theoryCourseContent: Array<TheoryLectureCategory>): TheoryLecture {
  if (theoryCourseContent.includes("Grundstoff")) {
    if (index < 12) {
      return { category: "Grundstoff", lectureNumber: index + 1 };
    } else {
      index -= 12;
    }
  }
  const zusatzstoffCategory = theoryCourseContent.find((it) => it !== "Grundstoff");
  if (!zusatzstoffCategory) {
    throw new Error("Invalid theoryCourseContent: " + JSON.stringify(theoryCourseContent));
  }
  return { category: zusatzstoffCategory, lectureNumber: index + 1 };
}
